import React from 'react';

/**
 * WYMUSZONY POZIOM NA TELEFONIE (Marcin, 2026-09-02).
 *
 * Szkolenie ma zajmowac caly ekran telefonu w poziomie — NIEZALEZNIE od tego, czy uzytkownik ma
 * odblokowany, czy zablokowany obrot ekranu. Systemowa blokada obrotu jest dla strony niewidzialna
 * i nieodwracalna z poziomu JS: `screen.orientation.lock()` dziala tylko na czesci Androidow i tylko
 * w pelnym ekranie, a na iOS nie istnieje w ogole. Jedyny sposob, ktory dziala WSZEDZIE, to obrocic
 * TRESC, a nie ekran — i to robi ten komponent.
 *
 * Wymiary bierzemy z `window.innerWidth/innerHeight`, nie z `100vh`: na mobilnych przegladarkach
 * `100vh` liczy sie razem z chowanym paskiem adresu, wiec kadr jest wyzszy niz widoczny obszar
 * i dol wideo wychodzi poza ekran.
 */

/** Ponizej tej krawedzi (krotszy bok ekranu) traktujemy urzadzenie jak telefon/tablet. */
export const MOBILNY_KROTSZY_BOK_PX = 820;

/**
 * Czysta matematyka kadru — bez DOM-u, zeby dalo sie ja przetestowac i zeby bylo widac, skad
 * biora sie liczby.
 *
 * @param {{width:number, height:number, dotykowy:boolean}} widok
 * @returns {null|{width:number, height:number, transform:string}} `null` = nie obracamy
 */
export function obliczKadrPoziomy({ width, height, dotykowy }) {
    if (!dotykowy) return null;                       // mysz/desktop — okno i tak jest poziome
    if (Math.min(width, height) > MOBILNY_KROTSZY_BOK_PX) return null; // duzy ekran dotykowy
    if (width >= height) return null;                 // telefon JUZ jest w poziomie

    // Obracamy tresc o 90° w prawo wokol lewego gornego rogu i cofamy ja o szerokosc ekranu,
    // zeby obrocony kadr trafil dokladnie w widoczny obszar:
    //   punkt (a,b) → ekran (width - b, a), czyli a∈[0,height] w dol, b∈[0,width] w prawo.
    return {
        width: height,   // dluzszy bok telefonu staje sie SZEROKOSCIA kadru
        height: width,   // krotszy bok — wysokoscia
        transform: `rotate(90deg) translateY(-${width}px)`,
    };
}

/**
 * Czy to TELEFON/TABLET — ten sam warunek, ktorym `obliczKadrPoziomy` decyduje o obrocie kadru:
 * palec (`pointer: coarse`) plus krotszy bok ekranu do `MOBILNY_KROTSZY_BOK_PX`.
 *
 * Jedna funkcja, bo od tej odpowiedzi zalezy TROJE zachowan odtwarzacza (pelny ekran, natywny
 * pasek wideo, ukrycie naszych przyciskow) — trzy niezalezne kopie tego warunku rozjechalyby sie
 * po pierwszej korekcie progu.
 */
export function czyUrzadzenieMobilne() {
    if (typeof window === 'undefined') return false;
    const dotykowy = typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
    if (!dotykowy) return false;
    const krotszyBok = Math.min(window.innerWidth || 0, window.innerHeight || 0);
    // `0` znaczy „okno jeszcze nie zmierzone" (jsdom/pierwszy render) — nie zgadujemy.
    return krotszyBok > 0 && krotszyBok <= MOBILNY_KROTSZY_BOK_PX;
}

class ForceLandscape extends React.Component {
    state = { width: 0, height: 0, dotykowy: false };

    componentDidMount() {
        this.zmierz();
        window.addEventListener('resize', this.zmierz);
        window.addEventListener('orientationchange', this.zmierz);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.zmierz);
        window.removeEventListener('orientationchange', this.zmierz);
        this.przywrocPrzewijanie();
    }

    componentDidUpdate() {
        // Obrocony kadr jest `position: fixed` — bez tego strona pod spodem daje sie przewijac
        // palcem i wideo ucieka za krawedz.
        if (this.kadr()) {
            document.body.style.overflow = 'hidden';
        } else {
            this.przywrocPrzewijanie();
        }
    }

    przywrocPrzewijanie() {
        document.body.style.overflow = '';
    }

    zmierz = () => {
        this.setState({
            width: window.innerWidth,
            height: window.innerHeight,
            // `pointer: coarse` = palec, nie mysz. Sam rozmiar okna nie wystarcza: okno przegladarki
            // na laptopie tez bywa waskie, a tam obracanie tresci byloby absurdem.
            dotykowy: typeof window.matchMedia === 'function'
                && window.matchMedia('(pointer: coarse)').matches,
        });
    };

    kadr() {
        const { width, height, dotykowy } = this.state;
        if (!width || !height) return null;
        return obliczKadrPoziomy({ width, height, dotykowy });
    }

    render() {
        const kadr = this.kadr();

        if (!kadr) {
            return this.props.children;
        }

        return (
            <div
                className="lt-kadr-poziomy"
                data-testid="kadr-poziomy"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: `${kadr.width}px`,
                    height: `${kadr.height}px`,
                    transform: kadr.transform,
                    transformOrigin: 'top left',
                    overflow: 'hidden',
                    zIndex: 1040,
                    background: '#000',
                }}
            >
                {this.props.children}
            </div>
        );
    }
}

export default ForceLandscape;
